// Angular
import { computed, inject, Injectable, signal } from '@angular/core';

// RxJS
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';

// Local
import { EmulatedProfile } from '@app/model/emulation.model';
import { EmulationApiService } from '@app/services/api/emulation/emulation-api.service';
import { IdentityClaimsService } from '@app/services/identity-claims/identity-claims.service';
import { FilingSummariesDataService } from '@feature/crl/services/filing-summaries-data.service';

/**
 * sessionStorage key holding the id of the user currently being emulated, so a page refresh keeps the
 * emulation active. Cleared when emulation ends.
 */
const EMULATION_TARGET_KEY = 'fsx.emulation.targetUserId';

/**
 * Client-side state for admin user-emulation. Holds the emulated target, drives the impersonation
 * header (via {@link targetUserId}), and overlays the target's identity onto the app while active.
 * All API data is scoped to the target on the server; this service keeps the UI consistent and
 * visibly indicates that emulation is in effect.
 */
@Injectable({ providedIn: 'root' })
export class EmulationService {
  private readonly emulationApi = inject(EmulationApiService);
  private readonly identityClaims = inject(IdentityClaimsService);
  private readonly filingSummariesDataService = inject(FilingSummariesDataService);

  /** The emulated user's profile, or null when not emulating. */
  private readonly emulatedUser = signal<EmulatedProfile | null>(null);

  /** Public read-only view of the emulated user. */
  readonly emulatedProfile = this.emulatedUser.asReadonly();

  /** True while an emulation session is active. */
  readonly isEmulating = computed<boolean>(() => this.emulatedUser() !== null);

  /** The id of the emulated user, used by the interceptor to attach the impersonation header. */
  readonly targetUserId = computed<string | null>(() => this.emulatedUser()?.userUniqueId ?? null);

  /** Cached in-flight restore so callers (app init + route guards) share one profile fetch. */
  private restore$: Observable<EmulatedProfile | null> | null = null;

  /**
   * Begins emulating the given user: fetches their effective profile, overlays their identity, and
   * persists the session so a refresh keeps it active. Errors are surfaced to the caller and leave the
   * admin un-emulated.
   *
   * @param userId The target user's unique id.
   */
  startEmulation(userId: string): Observable<EmulatedProfile> {
    this.filingSummariesDataService.clearDocumentTitleOverrides();
    return this.emulationApi.getProfile(userId).pipe(
      tap((profile: EmulatedProfile) => this.applyProfile(profile, userId)),
    );
  }

  /**
   * Ends emulation: restores the admin's real identity and clears the persisted session.
   */
  stopEmulation(): void {
    this.emulatedUser.set(null);
    this.identityClaims.clearEmulation();
    sessionStorage.removeItem(EMULATION_TARGET_KEY);
    this.filingSummariesDataService.clearDocumentTitleOverrides();
    this.restore$ = null;
  }

  /**
   * Re-applies emulation after a page refresh if a target is persisted. Must be called after the real
   * identity claims have been loaded so the overlay caches the correct admin claims. A failed
   * restoration (e.g. permission revoked) silently clears the session.
   */
  restoreFromSession(): Observable<EmulatedProfile | null> {
    if (this.restore$) {
      return this.restore$;
    }

    const userId = sessionStorage.getItem(EMULATION_TARGET_KEY);
    if (!userId) {
      this.restore$ = of(null);
      return this.restore$;
    }

    this.restore$ = this.emulationApi.getProfile(userId).pipe(
      tap((profile: EmulatedProfile) => this.applyProfile(profile, userId)),
      catchError(() => {
        this.stopEmulation();
        return of(null);
      }),
      shareReplay(1),
    );
    return this.restore$;
  }

  /**
   * Completes once any persisted emulation has been restored — or immediately if emulation is already
   * active or none is persisted. Route guards await this so identity-dependent decisions (e.g. the home
   * dashboard dispatch) see the emulated user rather than the admin after a reload.
   */
  ensureReady(): Observable<EmulatedProfile | null> {
    if (this.isEmulating()) {
      return of(this.emulatedProfile());
    }
    return this.restoreFromSession();
  }

  private applyProfile(profile: EmulatedProfile, userId: string): void {
    this.emulatedUser.set(profile);
    this.identityClaims.applyEmulation(profile);
    sessionStorage.setItem(EMULATION_TARGET_KEY, userId);
  }
}
